import type { EngineScore } from "@onchainreps/chess";
import type { MoveReview, PositionAnalysis } from "../lib/analysis";

interface EvalCurveProps {
  positions: PositionAnalysis[];
  reviews: MoveReview[];
  currentPly: number;
  total: number;
}

const W = 600;
const H = 100;

export function EvalCurve({ positions, reviews, currentPly, total }: EvalCurveProps) {
  const span = Math.max(total, 1);

  const points = positions.map((position, index) => {
    const x = (index / span) * W;
    const y = H - (position.winPercentWhite / 100) * H;
    return { x, y, ply: index };
  });

  const current = points.find((point) => point.ply === currentPly) ?? null;

  const linePath = points
    .map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
    .join(" ");

  const areaPath =
    points.length > 1
      ? `${linePath} L${points[points.length - 1]?.x.toFixed(1)} ${H} L${points[0]?.x.toFixed(1)} ${H} Z`
      : "";

  const blunderByPly = new Map(reviews.filter((r) => r.category === "blunder").map((r) => [r.ply + 1, r]));

  const focus = positions.find((position) => position.ply === currentPly) ?? positions[positions.length - 1];
  const evalText = focus ? formatEval(focus.score, focus.sideToMove) : "—";

  return (
    <section className="curve" aria-label="Evaluation graph">
      <div className="curve__head">
        <span className="curve__title">Evaluation</span>
        <span className="curve__value">{evalText}</span>
      </div>
      <svg className="curve__svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img">
        <line className="curve__mid" x1="0" y1={H / 2} x2={W} y2={H / 2} vectorEffect="non-scaling-stroke" />
        {areaPath ? <path className="curve__area" d={areaPath} /> : null}
        {linePath ? (
          <path className="curve__line" d={linePath} vectorEffect="non-scaling-stroke" />
        ) : null}
        {points.map((point) => {
          const review = blunderByPly.get(point.ply);
          if (!review) return null;
          return (
            <circle
              key={`blunder-${point.ply}`}
              className="curve__blunder"
              cx={point.x}
              cy={point.y}
              r={3.2}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        {current ? (
          <>
            <line className="curve__cursor" x1={current.x} y1="0" x2={current.x} y2={H} vectorEffect="non-scaling-stroke" />
            <circle className="curve__marker" cx={current.x} cy={current.y} r={3.6} vectorEffect="non-scaling-stroke" />
          </>
        ) : null}
      </svg>
      <div className="curve__legend">
        <span>start</span>
        <span>
          {positions.length}/{span} positions
        </span>
        <span>end</span>
      </div>
    </section>
  );
}

function formatEval(score: EngineScore | null, sideToMove: "white" | "black"): string {
  if (!score) return "—";
  const sign = sideToMove === "white" ? 1 : -1;
  if (score.type === "mate") {
    const moves = score.value * sign;
    return moves > 0 ? `M${moves}` : `-M${Math.abs(moves)}`;
  }
  const centipawns = (score.value * sign) / 100;
  const rounded = Math.abs(centipawns) >= 10 ? centipawns.toFixed(0) : centipawns.toFixed(2);
  return centipawns >= 0 ? `+${rounded}` : rounded;
}
